import { useCallback } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { CaseDetail, CaseUpdate, Money } from '../api/client'
import { useHasPermission } from '../api/session'
import { usePageActions, usePageTitle } from '../AppLayout'
import { useItemQuery } from '../hooks/useItemQuery'
import { useZielwaehrung } from '../hooks/useZielwaehrung'
import { Card } from '../components/card/Card'
import { CardGrid } from '../components/card/CardGrid'
import { BelegeKarte } from '../components/common/Belege'
import { Chip } from '../components/common/Chip'
import { EmptyState, ErrorBox, Loading, Note }
  from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { ContentWidth } from '../components/layout/ContentWidth'
import { Geld } from './priceFormat'
import type { Zahlform } from './priceFormat'
import { DetailSection } from '../components/detail/DetailSection'
import { Field } from '../components/detail/FieldGrid'
import { Callout } from '../components/research/Callout'
import { LineItem, LineList } from '../components/research/LineList'
import { Timeline, TimelineEntry } from '../components/research/Timeline'
import type { TimelineTone } from '../components/research/Timeline'
import { label, period } from './researchVocabulary'
import s from './ResearchDetailPage.module.scss'

const PERMISSION = 'research:read'

const datum = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' })
const kurz = new Intl.DateTimeFormat('de-DE',
                                     { day: '2-digit', month: '2-digit',
                                       year: 'numeric' })

const tag = (iso: string | null | undefined) =>
  iso ? datum.format(new Date(iso)) : undefined

/**
 * Ein Betrag in Millionen oder Milliarden.
 *
 * Bei möglichen Belastungen im Milliardenbereich sind alle Stellen weder
 * lesbar noch belastbar — die Zahl ist eine Schätzung, keine Rechnung.
 */
const gekuerzt: Zahlform = (value, currency) => {
  const zahl = Number(value)
  const einheit = currency ? ` ${currency}` : ''
  if (Math.abs(zahl) >= 1e9) {
    return `${(zahl / 1e9).toLocaleString('de-DE',
      { maximumFractionDigits: 2 })} Mrd${einheit}`
  }
  if (Math.abs(zahl) >= 1e6) {
    return `${(zahl / 1e6).toLocaleString('de-DE',
      { maximumFractionDigits: 1 })} Mio${einheit}`
  }
  return `${zahl.toLocaleString('de-DE')}${einheit}`
}

/**
 * Ein Betrag der Belastung samt Umrechnung.
 *
 * BEIDE GEKÜRZT: „≈ 857,3 Mio EUR" über „1 Mrd USD". Eine Schätzung, die als
 * Spanne von 1,5 bis 4 Milliarden angegeben ist, auf den Cent genau
 * umzurechnen wäre eine falsche Genauigkeit.
 *
 * `links` NICHT gesetzt: Die Belastung steht in einer Zeile, die das Etikett
 * links und den Wert rechts hat — beide Zeilen müssen rechts fluchten.
 */
function geld(wert: Money | null): ReactNode {
  if (wert === null) return '—'
  return <Geld wert={wert} schreibe={gekuerzt} />
}

export function ResearchDetailPage() {
  const allowed = useHasPermission(PERMISSION)
  const { id = '' } = useParams()
  const location = useLocation()
  const zurueck = (location.state as { from?: string } | null)?.from ?? ''

  // `useCallback` mit `id` als Abhängigkeit: `useItemQuery` hat die
  // Ladefunktion als Abhängigkeit, und eine bei jedem Rendern neue Funktion
  // bräche den eigenen Aufruf sofort wieder ab.
  const { waehrung, wahl } = useZielwaehrung(allowed)

  const { item, loading, error, denied, notFound } = useItemQuery<CaseDetail>(
    useCallback((signal: AbortSignal) => api.researchCase(
      Number(id), waehrung ? { currency: [waehrung] } : {}, signal),
      [id, waehrung]),
    allowed)

  // Der Titel des Falls steht in der Kopfzeile, nicht "Recherche": Wer eine
  // Detailseite offen hat, sieht dort, um welchen Vorgang es geht.
  usePageTitle(item?.title ?? 'Recherche', item?.reference ?? undefined)

  usePageActions(wahl)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION} subject="die Recherche" />
  }

  return (
    <ContentWidth>
      <p className={s.zurueck}>
        <Link to={`/research${zurueck}`}>← zurück zur Liste</Link>
      </p>

      {loading && <Card span={12}><Loading>lädt Case …</Loading></Card>}
      {error && !notFound && <ErrorBox>{error}</ErrorBox>}
      {notFound && (
        <Card span={12}>
          <EmptyState title="Case nicht vorhanden">
            Zu dieser Kennung gibt es keinen Vorgang. Möglicherweise wurde er
            zurückgezogen.
          </EmptyState>
        </Card>
      )}

      {item && <Fall fall={item} />}
    </ContentWidth>
  )
}

function Fall({ fall }: { fall: CaseDetail }) {
  return (
    <>
      <Kopf fall={fall} />

      <CardGrid>
        <DetailSection title="Stand" span={6}>
          <Field label="Art" value={label('case_type', fall.case_type)} />
          <Field label="Zuständig"
                 value={[fall.authority, fall.jurisdiction]
                          .filter(Boolean).join(' · ') || undefined} />
          <Field label="Betroffene Titel" wide
                 value={fall.instruments.length === 0 ? undefined : (
                   <span className={s.titel}>
                     {fall.instruments.map((i) => (
                       <span key={i.id} className={s.eintrag}>
                         <Link to={`/instruments/${i.id}`}
                               title={i.note ?? i.name}>{i.name}</Link>
                         <span className={s.rolle}>
                           {label('instrument_role', i.role)}
                         </span>
                       </span>
                     ))}
                   </span>
                 )}
                 help="Ein Kartellverfahren trifft eine ganze Branche, eine Übernahme mindestens zwei Unternehmen. Die Rolle sagt, wie der Titel betroffen ist." />
          <Field label="Beginn" value={tag(fall.opened_at)} mono
                 help="Wann der Vorgang begann — nicht, wann wir ihn erfasst haben." />
          <Field label="Wirkung auf den Handel"
                 value={`${fall.regime_impact} von 4 · ${label('trading_impact', fall.trading_impact)}`}
                 help="0 ohne Belang, 1 gering, 2 erhöhte Schwankung möglich, 3 Neubewertung möglich, 4 Kurskorridor unzuverlässig. Ausdrücklich KEINE Verrechnung mit einem Score — ein Punktwert würde ein kritisches Risiko wegmitteln." />
          <Field label="Wahrscheinlichkeit"
                 value={fall.probability === null ? undefined : (
                   <>
                     {Number(fall.probability).toFixed(0)} %
                     {fall.confidence !== null && (
                       <span className={s.nebenwert}>
                         {' '}· Sicherheit {fall.confidence} %
                       </span>
                     )}
                   </>
                 )}
                 help="Zwei verschiedene Fragen: „60 % wahrscheinlich“ bei hoher Sicherheit ist etwas anderes als dieselben 60 % beim Raten." />
          <Field label="Nächste Prüfung" value={tag(fall.next_review_at)}
                 mono />
        </DetailSection>

        <Card title="Mögliche Belastung" span={6}
              meta={fall.exposure_currency ?? undefined}
              help="Eine Spanne, keine Zahl: Eine Punktschätzung täuscht eine Genauigkeit vor, die es nicht gibt.">
          {fall.exposure_base === null ? (
            <Note>Noch nicht abgeschätzt.</Note>
          ) : (
            <>
              <dl className={s.spanne}>
                <div><dt>günstig</dt>
                  <dd>{geld(fall.exposure_low)}</dd></div>
                <div data-betont><dt>wahrscheinlich</dt>
                  <dd>{geld(fall.exposure_base)}</dd></div>
                <div><dt>ungünstig</dt>
                  <dd>{geld(fall.exposure_high)}</dd></div>
                <div><dt>Grundlage</dt>
                  <dd>{label('exposure_basis', fall.exposure_basis)}</dd></div>
                {fall.company_provision !== null && (
                  <div><dt>Rückstellung der Firma</dt>
                    <dd>{geld(fall.company_provision)}</dd></div>
                )}
              </dl>
              <Note>
                Was das Unternehmen zurückgestellt hat, steht getrennt von dem,
                was wir schätzen — die beiden Zahlen bedeuten Verschiedenes.
              </Note>
            </>
          )}
        </Card>
      </CardGrid>

      {(fall.summary || fall.analyst_assessment) && (
        <Card title="Sachstand und Einschätzung" span={12}>
          {fall.summary && <p className={s.fliesstext}>{fall.summary}</p>}
          {fall.analyst_assessment && (
            <Callout title="Einschätzung">{fall.analyst_assessment}</Callout>
          )}
        </Card>
      )}

      {/*
        Der Ausgang steht VOR dem Verlauf, sobald es einen gibt. Bei einem
        abgeschlossenen Case ist er die Antwort auf die Frage, mit der man ihn
        überhaupt aufschlägt — nicht eine Fußnote unter der Historie.
      */}
      {fall.outcome_summary && <Ausgang fall={fall} />}

      <Card title="Verlauf" span={12}
            meta={`${fall.updates.length} ${fall.updates.length === 1 ? 'Eintrag' : 'Einträge'}`}
            help="Jeder Eintrag hält fest, wie sich die Einschätzung DURCH dieses Ereignis verschoben hat.">
        {fall.updates.length === 0 ? (
          <Note>Noch keine Entwicklung erfasst.</Note>
        ) : (
          <Timeline>
            {fall.updates.map((u) => <Eintrag key={u.id} update={u} />)}
          </Timeline>
        )}
      </Card>

      {/*
        DASSELBE BAUTEIL WIE BEI MELDUNGEN, EREIGNISSEN UND IDEEN. Die Fälle
        tragen mehr je Zeile — Quellenart, Amtlichkeit, Datum, Zitat —, die
        Darstellung ist dieselbe.
      */}
      <BelegeKarte
        help="Amtliche Quellen sind gekennzeichnet: Eine Pressemeldung über ein Urteil ist etwas anderes als das Urteil selbst."
        belege={fall.sources.map((q) => ({
          id: q.id, titel: q.title, url: q.url,
          marke: label('source_type', q.source_type), amtlich: q.official,
          neben: q.published_at ? kurz.format(new Date(q.published_at)) : null,
          original: q.primary_source, zitat: q.excerpt,
        }))} />

      <CardGrid>
        {fall.catalysts.length > 0 && (
          <Card title="Erwartete Termine" span={6}
                help="Die Genauigkeit gehört zur Angabe: „Q4 2026“ heißt Q4 2026 und nicht der 15. Oktober.">
            <LineList>
              {fall.catalysts.map((k) => (
                <LineItem key={k.id} leadWidth="150px"
                          lead={period(k.date_from, k.date_to,
                                       k.date_precision)}
                          leadTone={k.confirmed ? 'neutral' : 'warn'}
                          aside={label('expectation_basis',
                                       k.expectation_basis)}
                          flag={k.confirmed ? null : 'erwartet'}
                          hint={k.title}>
                  {k.title}
                </LineItem>
              ))}
            </LineList>
          </Card>
        )}

        {fall.actions.length > 0 && (
          <Card title="Nächste Schritte" span={6}
                help="Ohne sie wäre die Recherche eine Ablage — ein Case ohne nächsten Schritt wird vergessen.">
            <LineList>
              {fall.actions.map((a) => (
                <LineItem key={a.id} leadWidth="112px"
                          leadTone={a.priority === 1 ? 'bad' : 'neutral'}
                          lead={a.trigger_type === 'DATE'
                            ? (a.due_at ? kurz.format(new Date(a.due_at)) : '—')
                            : a.trigger_type === 'RECURRING'
                              ? label('frequency', a.frequency)
                              : 'bei Ereignis'}
                          aside={a.status === 'OPEN' ? null
                                                     : a.status.toLowerCase()}
                          hint={a.description
                                || label('action_type', a.action_type)}>
                  {a.description || label('action_type', a.action_type)}
                </LineItem>
              ))}
            </LineList>
          </Card>
        )}
      </CardGrid>
    </>
  )
}

/** Titel, Aktenzeichen und der Stand als Pille. */
function Kopf({ fall }: { fall: CaseDetail }) {
  return (
    <Card span={12}>
      <div className={s.kopf}>
        <div className={s.kennung}>
          <h2>{fall.title}</h2>
          {fall.reference && (
            <span className={s.aktenzeichen}>{fall.reference}</span>
          )}
        </div>
        <div className={s.marken}>
          <Chip tone={fall.active ? 'info' : 'neutral'}>
            {label('case_status', fall.status)}
          </Chip>
          {/*
            Die Verfahrensstufe steht nur da, wenn sie MEHR sagt als der
            Stand: Bei "günstig beendet · geschlossen" wiederholt sie ihn.
          */}
          {fall.stage && fall.active && (
            <Chip>{label('case_stage', fall.stage)}</Chip>
          )}
        </div>
      </div>
    </Card>
  )
}

/** Der Ausgang eines abgeschlossenen Falls. */
function Ausgang({ fall }: { fall: CaseDetail }) {
  const ton = fall.outcome_vs_expectation === 'BETTER' ? 'good'
    : fall.outcome_vs_expectation === 'WORSE' ? 'bad'
    : 'neutral'

  // Die Wahrscheinlichkeit VOR dem Abschluss, nicht die danach: Beim
  // Schliessen steht sie auf 100 oder 0 — "besser als erwartet, gemessen an
  // 100 %" waere sinnlos. Gemeint ist, was wir schaetzten, solange es offen war.
  const abschluss = fall.updates.find(
    (u) => u.update_type === 'FINAL_RESOLUTION'
           && u.probability_before !== null)

  return (
    <Card title="Ausgang" span={12} meta={tag(fall.resolved_at)}>
      <p className={s.fliesstext}>{fall.outcome_summary}</p>
      {fall.outcome_vs_expectation && (
        <p className={s.bewertung}>
          <Chip tone={ton}>
            {label('outcome', fall.outcome_vs_expectation)}
          </Chip>
          {abschluss && (
            <span className={s.nebenwert}>
              geschätzt hatten wir{' '}
              {Number(abschluss.probability_before).toFixed(0)} %
            </span>
          )}
        </p>
      )}
      {fall.lessons && (
        <Callout tone="note" title="Für den nächsten Case">
          {fall.lessons}
        </Callout>
      )}
    </Card>
  )
}

/** Ein Eintrag im Verlauf. */
function Eintrag({ update }: { update: CaseUpdate }) {
  const ton: TimelineTone = update.direction === 'POSITIVE' ? 'good'
    : update.direction === 'NEGATIVE' ? 'bad'
    : update.direction === 'UNCLEAR' ? 'unclear'
    : 'neutral'

  const wann = update.event_at ?? update.recorded_at
  // Der Erfassungszeitpunkt steht nur da, wenn es ein anderer Tag war —
  // sonst wäre er eine Wiederholung.
  const erfasst = update.event_at
    && update.recorded_at.slice(0, 10) !== update.event_at
    ? kurz.format(new Date(update.recorded_at))
    : undefined

  const verschiebung = update.probability_before !== null
    && update.probability_after !== null
    && update.probability_before !== update.probability_after
    ? `${Number(update.probability_before).toFixed(0)} → ${Number(update.probability_after).toFixed(0)} %`
    : null

  return (
    <TimelineEntry
      date={kurz.format(new Date(wann))}
      recorded={erfasst}
      tone={ton}
      headline={update.headline}
      comment={update.analyst_comment ?? undefined}
      meta={
        <>
          {label('update_type', update.update_type)}
          {update.materiality !== null
            && ` · Bedeutung ${update.materiality}/4`}
          {verschiebung && ` · Wahrscheinlichkeit ${verschiebung}`}
        </>
      }>
      {update.fact_summary}
    </TimelineEntry>
  )
}
